import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Loader2, LockKeyhole } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/presentation`;
const fill = (s, v) => s.replace(/\{(\w+)\}/g, (_, k) => v[k]);

const Viewer = ({ url, onClose, p }) => {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !document.fullscreenElement) closeRef.current(); };
    const onFs = () => { if (!document.fullscreenElement && ref.current?.dataset.fs === "1") closeRef.current(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    document.documentElement.style.overflow = "hidden";
    const el = ref.current;
    el?.requestFullscreen?.().then(() => { el.dataset.fs = "1"; }).catch(() => {});
    return () => {
      if (el) el.dataset.fs = "0";
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      document.documentElement.style.overflow = "";
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  return (
    <div ref={ref} className="fixed inset-0 z-[200] bg-[#050b17]" data-testid="presentation-viewer">
      {url ? (
        <iframe src={url} title="Презентация" className="w-full h-full border-0" allow="fullscreen" data-testid="presentation-iframe" />
      ) : (
        <div className="h-full grid place-items-center text-[var(--muted)] text-sm"><span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-[var(--gold)]" />{p.loading}</span></div>
      )}
      <button onClick={onClose} aria-label={p.close} data-testid="presentation-close-button"
        className="absolute top-2 left-1/2 -translate-x-1/2 w-9 h-9 grid place-items-center rounded-full bg-black/60 border border-white/20 text-white hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export const PresentationGate = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const p = t.pres;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(false);
  const [url, setUrl] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setCode(""); setError(""); } }, [open]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const close = () => { setViewing(false); setUrl(""); };

  const submit = async (value) => {
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/unlock`, { code: value }, { withCredentials: true });
      onOpenChange(false);
      setViewing(true);
      const { data } = await axios.get(`${API}/file`, { responseType: "blob", withCredentials: true });
      setUrl(URL.createObjectURL(new Blob([data], { type: "text/html" })));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "locked") setError(fill(p.locked, { m: Math.ceil((d.retry_after || 900) / 60) }));
      else if (d?.code === "wrong") setError(fill(p.wrong, { n: d.attempts_left ?? 0 }));
      else { setError(p.error); setViewing(false); }
      setCode("");
      setTimeout(() => inputRef.current?.focus(), 30);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0F131C] border border-[var(--gold)]/30 text-[var(--text)] rounded-3xl max-w-sm" data-testid="presentation-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><LockKeyhole className="w-5 h-5 text-[var(--gold)]" />{p.title}</DialogTitle>
            <DialogDescription className="text-[var(--muted)]">{p.sub}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <InputOTP ref={inputRef} maxLength={4} value={code} onChange={setCode} onComplete={submit} disabled={busy} inputMode="numeric" pattern="^[0-9]*$" autoFocus data-testid="presentation-code-input">
              <InputOTPGroup className="gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <InputOTPSlot key={i} index={i} className="w-14 h-16 text-2xl font-display rounded-2xl border border-white/20 first:rounded-2xl last:rounded-2xl first:border-l" />
                ))}
              </InputOTPGroup>
            </InputOTP>
            <div className="min-h-[20px] text-sm text-center" aria-live="polite">
              {busy && <span className="inline-flex items-center gap-2 text-[var(--muted)]"><Loader2 className="w-4 h-4 animate-spin" />{p.checking}</span>}
              {!busy && error && <span className="text-red-400" data-testid="presentation-error">{error}</span>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {viewing && createPortal(<Viewer url={url} onClose={close} p={p} />, document.body)}
    </>
  );
};
